import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { trackConversion } from '@/lib/analytics';
import {
  REVIEW_MIN_COMPLETED_PRODUCTS,
  resolveReviewPlatform,
  reviewWriteUrl,
  type ReviewPlatform,
} from '@/lib/reviewPlatform';

/**
 * Die In-App-Bitte um eine Bewertung (Review-Pipeline, Spec 2026-09-05, E14 bis E16).
 *
 * Sichtbar, wenn die eigene Profilzeile `review_state IS NULL OR 'asked'` traegt,
 * weder geklickt noch abgelehnt wurde UND mindestens ein manueller Lauf mit
 * >= REVIEW_MIN_COMPLETED_PRODUCTS Produkten abgeschlossen ist (derselbe
 * Wertmoment wie die Mail, D4). BEWUSST OHNE is_internal-Pruefung: interne
 * Konten sehen die Karte (E2E-testbar), bekommen aber keine Mail, weil nur der
 * Sweep filtert.
 *
 * Plattform: `review_platform`, falls der Sweep sie schon gesetzt hat, sonst die
 * gespiegelte Regel D3 aus country_code + UI-Sprache. Beide Aktionen sind ein
 * Compare-and-Set auf die eigene Zeile (D5, Muster useConversionMilestones);
 * Fehler werden still geschluckt, eine Bitte darf nie einen Nutzerpfad brechen.
 *
 * Ruling aus dem PR2-Final-Review (bindend): der Dismiss-Pfad schreibt
 * zusaetzlich `review_platform` (aus derselben Spiegelregel) mit — die
 * Montags-Wochenzeile des Sweeps zaehlt Klicks UND Dismisses je Plattform
 * (`.eq('review_platform', platform)`); ohne die Plattform am Dismiss stuende
 * dort dauerhaft "Abgelehnt: OMR 0 / G2 0".
 */
export interface ProfileReviewRow {
  review_state: string | null;
  review_platform: string | null;
  review_clicked_at: string | null;
  review_dismissed_at: string | null;
  country_code: string | null;
}

export function reviewCardVisible(row: ProfileReviewRow | null, hasWertmoment: boolean): boolean {
  if (!row || !hasWertmoment) return false;
  if (row.review_clicked_at || row.review_dismissed_at) return false;
  return row.review_state === null || row.review_state === 'asked';
}

export interface ReviewRequestState {
  visible: boolean;
  platform: ReviewPlatform;
  writeUrl: string;
  markClicked: () => Promise<void>;
  dismiss: () => Promise<void>;
  pending: boolean;
}

interface ReviewQueryData { row: ProfileReviewRow | null; hasWertmoment: boolean; }

export function useReviewRequest(): ReviewRequestState {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  // Lokale Sperre nach einer Aktion: die Karte verschwindet sofort, die DB traegt
  // die Dauerhaftigkeit (Reload liest die Marke).
  const [done, setDone] = useState(false);

  const query = useQuery<ReviewQueryData>({
    queryKey: ['review-request', userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('review_state, review_platform, review_clicked_at, review_dismissed_at, country_code')
        .eq('user_id', userId!)
        .maybeSingle();
      if (error) return { row: null, hasWertmoment: false };
      // head-Count der eigenen Wertmoment-Laeufe (RLS scoped auf den Nutzer, der
      // eq ist die ausgeschriebene Absicht).
      const { count, error: countError } = await supabase
        .from('optimization_jobs')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId!)
        .eq('status', 'completed')
        .is('triggered_by', null)
        .gte('completed_products', REVIEW_MIN_COMPLETED_PRODUCTS);
      // Die Spalten fehlen im generierten Supabase-Typ bis zum naechsten Typegen
      // (gleiche Lage wie conversion_* in useConversionMilestones).
      return { row: (profile as unknown as ProfileReviewRow | null) ?? null, hasWertmoment: !countError && (count ?? 0) > 0 };
    },
  });

  const row = query.data?.row ?? null;
  const visible = !done && reviewCardVisible(row, query.data?.hasWertmoment ?? false);
  const platform: ReviewPlatform = row?.review_platform === 'omr' || row?.review_platform === 'g2'
    ? row.review_platform
    : resolveReviewPlatform(row?.country_code ?? null, i18n.language);
  const writeUrl = reviewWriteUrl(platform, i18n.language, 'app');

  const finish = useCallback(() => {
    setDone(true);
    void queryClient.invalidateQueries({ queryKey: ['review-request', userId] });
  }, [queryClient, userId]);

  const markClicked = useCallback(async () => {
    if (!userId) return;
    try {
      // Compare-and-Set. Nur wer eine Zeile zurueckbekommt, hat den Klick
      // tatsaechlich als Erster vermerkt (Muster useConversionMilestones);
      // ein zweiter Tab, der auf eine bereits markierte Zeile trifft, bekommt
      // `data: []` und darf nicht ein zweites Mal melden.
      const { data, error } = await supabase
        .from('profiles')
        // Cast nur fuer die Typpruefung, das gesendete JSON ist unveraendert.
        .update({ review_clicked_at: new Date().toISOString(), review_state: 'clicked', review_platform: platform } as never)
        .eq('user_id', userId)
        .is('review_clicked_at', null)
        .select('user_id');
      if (!error && Array.isArray(data) && data.length > 0) {
        trackConversion('cta_click', { location: 'review_card', label: `review_${platform}` });
      }
    } catch {
      // still: die Navigation zum Formular laeuft ueber den Anker, unabhaengig von dieser Marke
    } finally {
      // Die Karte verschwindet lokal in jedem Fall — verloren hat das CAS nur,
      // wenn die Marke schon stand, und dann gehoert die Karte ohnehin weg.
      finish();
    }
  }, [userId, platform, finish]);

  const dismiss = useCallback(async () => {
    if (!userId) return;
    try {
      await supabase
        .from('profiles')
        // Ruling PR2-Final-Review: review_platform muss auch hier mitlaufen,
        // sonst zaehlt die Wochenzeile "Abgelehnt: OMR 0 / G2 0" auf Dauer.
        .update({ review_dismissed_at: new Date().toISOString(), review_state: 'dismissed', review_platform: platform } as never)
        .eq('user_id', userId)
        .is('review_dismissed_at', null)
        // M2 (Final-Review PR3): `clicked` bleibt terminal. Ohne diese
        // Bedingung wuerde ein stehen gebliebener zweiter Tab einen bereits
        // gezaehlten Klick nachtraeglich zum Dismiss herabstufen -- der
        // Wochen-Sweep zaehlt beide Spalten je Plattform.
        .is('review_clicked_at', null)
        .select('user_id');
    } catch {
      // still, siehe oben
    } finally {
      finish();
    }
  }, [userId, platform, finish]);

  return { visible, platform, writeUrl, markClicked, dismiss, pending: query.isPending };
}
